import * as React from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useRoute } from 'wouter';
import { CmdSubpageButton } from '@commander-services/gui-components';
import { useQueries } from '@tanstack/react-query';
import {
  URL_TRIP_MAP,
  URL_TRIPS_OVERVIEW,
  URL_TRIPS_OVERVIEW_WITH_ECO_DRIVE,
} from '../../../router/constants';
import { localForageStore } from '../../../store/localForageStore';
import usePageWithForm from '../../../hooks/usePageWithForm';
import EcoDriveOnline from './EcoDriveOnline';
import GdprService from '../../../Services/GdprService';
import PermissionsService, {
  PERMISSION_RIDE_SHOW_AND_PLAY,
} from '../../../Services/PermissionsService';
import { getEcodriveData } from '../../TripsOverviewNew/TripsOverviewService';
import useAnalytics from '../../../hooks/useAnalytics';

interface IProps {
  closeCallback?: () => void;
  redirectCallback?: () => void;
}

function EcoDriveSubpage(props: IProps) {
  usePageWithForm();
  const { trackEvent } = useAnalytics();
  const { formatMessage: f } = useIntl();
  const [, navigate] = useLocation();
  const [, params] = useRoute(`${URL_TRIPS_OVERVIEW_WITH_ECO_DRIVE}/:vehicleId/:rideId`);

  const [isGdprAlert, setIsGdprAlert] = React.useState(false);
  const [customerId, setCustomerId] = React.useState<number | null>(null);
  const [vehicleId, setVehicleId] = React.useState<number | null>(null);
  const [driverId, setDriverId] = React.useState<number | null>(null);
  const [isPrivate, setIsPrivate] = React.useState<boolean>(true);
  const [rideId, setRideId] = React.useState<string | null>(null);
  const [isGdprAllowed, setIsGdprAllowed] = React.useState(false);

  React.useEffect(() => {
    if (params) {
      setRideId(String(params.rideId) || null);
      setVehicleId(Number(params.vehicleId) || null);
    }
  }, [params]);

  const redirectToTripMap = () => {
    if (customerId && rideId) {
      trackEvent(['Ecodrive graph', 'click', 'Redirect to trip map']);
      localForageStore.setItem('rides', [`${customerId}-${rideId}`]);
      localForageStore.setItem('multi-rides', null);
      window.open(`${URL_TRIP_MAP}`, '_blank');
    }
  };

  const closeSubpage = () => {
    trackEvent(['Ecodrive graph', 'click', 'Close Ecodrive graph']);
    navigate(URL_TRIPS_OVERVIEW);
  };

  const getSubpageButtonsData = async () => {
    if (vehicleId && rideId) {
      const data = await getEcodriveData(vehicleId, rideId);
      if (data) {
        setCustomerId(data.customerId);
        setIsPrivate(data.isPrivate);
        setDriverId(data.driverId);
        const result = GdprService.getRideGdpr(
          'showPrivateData',
          data.isPrivate,
          data.customerId,
          data.driverId
        );
        if (!result) {
          setIsGdprAlert(true);
        }
      }
    }
  };

  useQueries({
    queries: [
      {
        queryKey: ['ecoDriveSubpage', vehicleId, rideId],
        queryFn: () => getSubpageButtonsData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const handleGdpr = React.useCallback(async () => {
    const result = await GdprService.getRideGdpr('showRide', isPrivate, customerId || 0, driverId);
    if (result !== undefined) {
      setIsGdprAllowed(result);
    }
  }, [customerId, driverId, isPrivate]);

  React.useEffect(() => {
    handleGdpr();
  }, [handleGdpr]);

  const subpageButtons: CmdSubpageButton[] =
    customerId &&
    isGdprAllowed &&
    PermissionsService.hasAccess(PERMISSION_RIDE_SHOW_AND_PLAY, ['read'], customerId)
      ? [
          {
            id: 'ecodrive-form-close',
            type: 'button',
            className: 'e-button',
            title: f({ id: 'suppliersForm.close' }),
            closeCallback: props.closeCallback || closeSubpage,
          },

          {
            id: 'ecodrive-redirect',
            type: 'button',
            className: 'e-button',
            title: f({ id: 'tripsOverview.modal.playRide' }),
            callback: props.redirectCallback || redirectToTripMap,
          },
        ]
      : [
          {
            id: 'ecodrive-form-close',
            type: 'button',
            className: 'e-button',
            title: f({ id: 'suppliersForm.close' }),
            closeCallback: props.closeCallback || closeSubpage,
          },
        ];

  return vehicleId && rideId ? (
    <EcoDriveOnline
      vehicleId={vehicleId}
      rideId={rideId}
      subpageButtons={subpageButtons}
      isGdprAlert={isGdprAlert}
    />
  ) : null;
}

export default React.memo(EcoDriveSubpage);
